<template>
  <div class="outer-container">
    <div class="inner-container">
      <div class="wrapper">
        <!-- 显示骨架屏或实际内容 -->
        <template v-if="loading && plainMatches.length === 0">
          <SkeletonLoader type="football-score" :count="12" />
        </template>
        <template v-else>
          <div class="score-container">
            <div class="top">
              <div class="category-list">
                <button
                  class="button"
                  :class="{ active: activeButton === '0' }"
                  @click="setActive('0')"
                >
                  即时
                </button>
                <button
                  class="button"
                  :class="{ active: activeButton === '3' }"
                  @click="setActive('3')"
                >
                  赛果
                </button>
                <button
                  class="button"
                  :class="{ active: activeButton === '1' }"
                  @click="setActive('1')"
                >
                  赛程
                </button>
                <button
                  class="button"
                  :class="{ active: activeButton === '2' }"
                  @click="setActive('2')"
                >
                  收藏
                </button>
              </div>
              <ul class="actions_list">
                <li class="action_item action_league_dropdown" @click.stop="toggleLeagueDropdown">
                  <span>赛事</span>
                  <img src="@assets/icons/score/more.webp" alt="下拉箭头" class="down-arrow" />
                  
                  <LeagueFilterDropdown
                    :visible="showLeagueDropdown"
                    :matches="plainMatches"
                    :current-filter="selectedFilterOption"
                    @update:visible="showLeagueDropdown = $event"
                    @confirm="handleLeagueConfirm"
                    @filter-change="handleFilterChange"
                  />
                </li>
                <!-- 新增完整按钮 -->
                <li class="action_item action_dropdown">
                  <FilterDropdown
                    :model-value="selectedFilterOption"
                    mode="dropdown"
                    @update:modelValue="handleFilterChange"
                  />
                </li>
                <li class="action_version_toggle">
                  <VersionToggle
                    v-model="versionMode"
                    theme="football"
                    @change="handleVersionChange"
                  />
                </li>

                <li 
                  class="action_item action_sound_dropdown" 
                  @click.stop="toggleSoundDropdown"
                  @mouseenter="handleSoundMouseEnter"
                  @mouseleave="showSoundDropdown = false"
                >
                  <span>{{ selectedSoundLabel }}</span>
                  <!-- 静音图标 -->
                  <svg v-if="selectedSound === 'mute'" class="sound-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11 5L6 9H2V15H6L11 19V5Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <line x1="23" y1="9" x2="17" y2="15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <line x1="17" y1="9" x2="23" y2="15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <!-- 音量图标 -->
                  <svg v-else class="sound-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11 5L6 9H2V15H6L11 19V5Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M15.54 8.46C16.4774 9.39764 17.0039 10.6692 17.0039 11.995C17.0039 13.3208 16.4774 14.5924 15.54 15.53" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M18.07 5.93C19.9447 7.80528 20.9979 10.3462 20.9979 13C20.9979 15.6538 19.9447 18.1947 18.07 20.07" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  
                  <SoundDropdown
                    :visible="showSoundDropdown"
                    :current-sound="selectedSound"
                    @update:visible="showSoundDropdown = $event"
                    @sound-change="handleSoundChange"
                  />
                </li>
                <li 
                  class="action_item action_settings_dropdown" 
                  @click.stop="toggleSettingsDropdown"
                  @mouseenter="handleSettingsMouseEnter"
                  @mouseleave="showSettingsDropdown = false"
                >
                  <span>设置</span>
                  <img src="@assets/icons/score/setting.webp" alt="设置" class="settings-icon" />
                  
                  <SettingsDropdown
                    :visible="showSettingsDropdown"
                    @update:visible="showSettingsDropdown = $event"
                    @settings-change="handleSettingsChange"
                  />
                </li>
              </ul>
            </div>

            <TabNavigation :menusList="menusList" @categorySelect="categorySelect" />
          </div>
          <div class="list" :class="{ 'refreshing': refreshing }">
            <template v-if="plainMatches.length > 0">
              <!-- 专业版布局 -->
              <template v-if="versionMode === 'professional'">
                <div class="list-scroll">
                  <MatchList
                    :list="plainMatches"
                    :hasReset="hasReset"
                    :selectedMatch="matchSelect.match"
                    :onSelectMatch="selectMatch"
                  />
                </div>
                <div class="match-detail">
                  <MatchDetails :match="matchSelect.match" />
                </div>
              </template>
              
              <!-- 标准版布局 -->
              <template v-else-if="versionMode === 'standard'">
                <div class="list-scroll list-scroll-full">
                  <MatchListStandard
                    :list="plainMatches"
                    :hasReset="hasReset"
                    :selectedMatch="matchSelect.match"
                    :onSelectMatch="selectMatch"
                  />
                </div>
              </template>
            </template>
            <template v-else>
              <div style="width: 100%; height: 100%">
                <div class="empty_list">
                  <img src="@assets/icons/score/empty.webp" alt="Empty" class="empty-image" />
                  <p>暂无数据</p>
                </div>
              </div>
            </template>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import TabNavigation from './TabNavigation.vue'
import MatchList from './MatchList.vue'
import MatchListStandard from './MatchListStandard.vue'
import MatchDetails from './MatchDetails.vue'
import LeagueFilterDropdown from './LeagueFilterDropdown.vue'
import FilterDropdown from './FilterDropdown.vue'
import SettingsDropdown from './SettingsDropdown.vue'
import SoundDropdown from './SoundDropdown.vue'
import VersionToggle from '@/components/common/VersionToggle.vue'
// import LoadingSpinner from '@/components/loading.vue'
import SkeletonLoader from '@/components/SkeletonLoader.vue'
// import { ref } from 'vue'
import { onMounted, ref, watch, computed, onUnmounted, reactive, nextTick } from 'vue'
import { getFootballMatchCategory } from '@/api/match'

import { getMatchFootballList } from '@/api/match'
import { fromJS, Map } from 'immutable'
import { getDQToken } from '@/api/user'

import dayjs from 'dayjs'
import pako from 'pako'
import axios from 'axios'
import { message } from 'ant-design-vue'
import { decompressData } from '@/utils/dataProcessing'

const menusList = ref([])
const hasReset = ref(false)
const loading = ref(true)
const activeButton = ref('0')
// 新增：数据刷新状态（用于快速切换时的轻量加载提示）
const refreshing = ref(false)

// 新增：筛选选项状态
const selectedFilterOption = ref('完整')

// 新增：赛事下拉菜单状态
const showLeagueDropdown = ref(false)

// 新增：设置下拉菜单状态
const showSettingsDropdown = ref(false)

// 新增：音效下拉菜单状态
const showSoundDropdown = ref(false)
const selectedSound = ref('default')

// 新增：版本模式状态
const versionMode = ref('professional')

/**
 * 筛选选项到 filter 值的映射表（足球专用）
 * 根据接口文档：足球 0完整 9热门 6竞彩 7北单
 */
const FILTER_VALUE_MAP = {
  '完整': 0,
  '热门': 9,
  '竞彩': 6,
  '北单': 7
}

/**
 * 根据选项文字获取对应的 filter 值
 * @param {string} option - 选项文字
 * @returns {number} filter 值，默认返回 0（完整）
 */
const getFilterValue = (option) => {
  return FILTER_VALUE_MAP[option] ?? 0
}

/**
 * 音效选项到标签的映射表
 */
const SOUND_LABEL_MAP = {
  'mute': '静音',
  'default': '默认',
  'whistle': '哨子',
  'drum': '鼓声',
  'broadcast': '广播',
  'horn': '号角',
  'victory': '胜利'
}

/**
 * 计算属性：获取当前选中音效的标签
 */
const selectedSoundLabel = computed(() => {
  return SOUND_LABEL_MAP[selectedSound.value] || '默认'
})

// 将match选择状态集中管理
const matchSelect = reactive({
  match: {},
  selectedMatchId: null
})

// 将列表数据和状态集中管理
const listState = reactive({
  list: fromJS([]),
  categoryId: null,
  state: null,
  init: false
})

// 新增：筛选条件状态
const filterState = reactive({
  type: null, // 'country' | 'league' | null
  matchIds: null, // Set of match IDs for country filter
  leagueNames: null // Array of league names for league filter
})

// 计算属性获取plain数组（应用筛选）
const plainMatches = computed(() => {
  const allMatches = listState.list.toJS()
  console.log('🔍 plainMatches 计算中...')
  console.log('  - 所有比赛数量:', allMatches.length)
  console.log('  - 筛选类型:', filterState.type)
  
  // 如果是国家筛选模式
  if (filterState.type === 'country' && filterState.matchIds && filterState.matchIds.size > 0) {
    console.log('  - 国家筛选模式激活')
    console.log('  - 筛选matchIds数量:', filterState.matchIds.size)
    console.log('  - 筛选matchIds:', Array.from(filterState.matchIds))
    
    // 打印前5个比赛的所有ID字段，便于调试
    if (allMatches.length > 0) {
      console.log('  - 前5个比赛的完整对象:', allMatches.slice(0, 5).map(m => ({
        match_id: m.match_id,
        id: m.id,
        matchId: m.matchId,
        matchID: m.matchID,
        match_ID: m.match_ID,
        title: m.title,
        '所有字段': Object.keys(m)
      })))
    }
    
    // 尝试寻找阿根廷的比赛
    const argentinaMatch = allMatches.find(m => 
      m.match_id === 12964864 || 
      m.id === 12964864 ||
      m.matchId === 12964864
    )
    if (argentinaMatch) {
      console.log('  - 找到匹配的阿根廷比赛:', argentinaMatch)
    } else {
      console.log('  - ❌ 未找到matchId为12964864的比赛')
      // 尝试看看比赛ID的实际格式
      console.log('  - 随机比赛示例:', allMatches[0])
    }
    
    const filtered = allMatches.filter(match => {
      const matchId = match.match_id || match.id
      // 尝试数字和字符串两种形式进行匹配
      const matchIdNum = Number(matchId)
      const matchIdStr = String(matchId)
      const isMatch = filterState.matchIds.has(matchId) || 
                      filterState.matchIds.has(matchIdNum) || 
                      filterState.matchIds.has(matchIdStr)
      if (isMatch) {
        console.log(`    ✅ 匹配: ${matchId} (${typeof matchId}) - ${match.title}`)
      }
      return isMatch
    })
    
    console.log('  - 筛选后比赛数量:', filtered.length)
    return filtered
  }
  
  // 如果是联赛筛选模式
  if (filterState.type === 'league' && filterState.leagueNames && filterState.leagueNames.length > 0) {
    return allMatches.filter(match => {
      const leagueName = match.title?.trim()
      return filterState.leagueNames.includes(leagueName)
    })
  }
  
  // 无筛选，返回所有比赛
  console.log('  - 无筛选，返回所有比赛')
  return allMatches
})

// 处理match选择
const selectMatch = (match) => {
  // 添加防御性检查
  if (!match) return

  matchSelect.match = match
  matchSelect.selectedMatchId = match.match_id
}

// 添加轮询间隔常量
const POLLING_INTERVAL = 30000 // 30秒轮询一次

// 轮询控制
const polling = ref(null)

// 获取列表数据
const fetchMatchList = async (isFilterChange = false) => {
  // 如果是首次加载且没有数据，显示骨架屏
  if (plainMatches.value.length === 0) {
    loading.value = true
  } else if (isFilterChange) {
    // 如果是切换filter，显示轻量刷新状态
    refreshing.value = true
  }

  try {
    // 如果是首次加载且已有默认数据，不显示loading，避免闪烁
    const hasExistingData = plainMatches.value.length > 0
    if (hasExistingData && !isFilterChange) {
      loading.value = false
    }
    // 获取当前选中的 filter 值
    const filterValue = getFilterValue(selectedFilterOption.value)
    
    // 构建请求参数，确保 filter 参数始终传递（即使是 0 也要传递）
    const params = {}
    
    // 只添加非空的参数
    if (listState.categoryId) {
      params.leagueId = listState.categoryId
    }
    if (listState.state !== null && listState.state !== undefined) {
      params.state = listState.state
    }
    // filter 参数始终传递，即使是默认值 0
    params.filter = filterValue
    
    console.log('ScorePage: 请求参数', params)
    console.log('ScorePage: 当前筛选选项', selectedFilterOption.value, 'filter值', filterValue)
    
    const res = await getMatchFootballList(params)
    if (res.code === 200) {
      const data = decompressData(res.data)
      if (data?.length) {
        console.log('ScorePage: 获取到数据', data.length, '条，筛选选项:', selectedFilterOption.value, 'filter:', filterValue)
        // 直接更新数据，使用 nextTick 确保DOM更新在下一帧，提升响应速度
        listState.list = fromJS(data)
        
        // 如果数据量大，使用 nextTick 分批更新；否则直接更新
        if (data.length > 500 && !isFilterChange) {
          await nextTick()
        }
        return true
      } else {
        console.log('ScorePage: 没有数据，筛选选项:', selectedFilterOption.value, 'filter:', filterValue)
        // 空数据直接更新，无需等待
        listState.list = fromJS([])
      }
    } else {
      // 如果响应码不是200，也保持已有数据，不清空
      console.log('ScorePage: 响应码不是200，保持已有数据')
    }
  } catch (error) {
    console.error('获取比赛列表失败:', error)
    // 网络错误时不清空已有数据，保持页面显示
    // 这样即使请求失败，之前加载的数据仍然可以显示
  } finally {
    loading.value = false
    refreshing.value = false
    
    // 🐾 打印当前比赛列表数据（详细版）
    console.log('='.repeat(80))
    console.log('📊 足球即时比分数据详情')
    console.log('='.repeat(80))
    const currentMatches = listState.list.toJS()
    console.log(`总比赛数: ${currentMatches.length}`)
    console.log(`筛选选项: ${selectedFilterOption.value}`)
    console.log(`状态: ${listState.state === 0 ? '即时' : listState.state === 1 ? '赛程' : listState.state === 3 ? '赛果' : '未知'}`)
    
    if (currentMatches.length > 0) {
      console.log('\n📋 前5场比赛详情:')
      currentMatches.slice(0, 5).forEach((match, index) => {
        console.log(`\n比赛 ${index + 1}:`)
        console.log(`  🆔 比赛ID: ${match.match_id}`)
        console.log(`  🏆 联赛: ${match.title}`)
        console.log(`  🏠 主队: ${match.home_team_name}`)
        console.log(`  ✈️  客队: ${match.away_team_name}`)
        console.log(`  ⚽ 比分: ${match.home_grades} - ${match.away_grades}`)
        console.log(`  📊 状态: ${match.match_status_txt} (status: ${match.status})`)
        console.log(`  🕐 时间: ${match.matchTime}`)
        console.log(`  🔥 热度: ${match.hot_desc || '无'}`)
        console.log(`  📺 直播: 视频=${match.has_vid === 1 ? '✅' : '❌'}, 动画=${match.has_anim === 1 ? '✅' : '❌'}`)
        console.log(`  📋 完整数据:`, match)
      })
      
      // 按状态分组统计
      const statusGroups = currentMatches.reduce((acc, match) => {
        const status = match.status
        acc[status] = (acc[status] || 0) + 1
        return acc
      }, {})
      
      console.log('\n📈 比赛状态统计:')
      console.log(`  ⏰ 未开始 (status=1): ${statusGroups[1] || 0} 场`)
      console.log(`  ▶️  进行中 (status=2): ${statusGroups[2] || 0} 场`)
      console.log(`  ✅ 已结束 (status=3): ${statusGroups[3] || 0} 场`)
    } else {
      console.log('⚠️ 当前无比赛数据')
    }
    console.log('='.repeat(80))
  }
  return false
}

// 处理match选中状态 - 优化逻辑
const handleMatchSelection = () => {
  if (!plainMatches.value.length) {
    resetMatchSelect()
    return
  }

  if (matchSelect.selectedMatchId) {
    // 在新数据中查找当前选中的match
    const selectedMatch = plainMatches.value.find(
      (match) => match.match_id === matchSelect.selectedMatchId
    )
    if (selectedMatch) {
      // 更新选中match的数据
      matchSelect.match = selectedMatch
    } else {
      // 如果找不到之前选中的match,选择第一个
      selectMatch(plainMatches.value[0])
    }
  } else {
    // 没有选中项时,选择第一个
    selectMatch(plainMatches.value[0])
  }
}

// 触发列表重置动画（优化：减少延迟）
const triggerListReset = () => {
  hasReset.value = true
  // 减少动画延迟，从100ms降到50ms
  setTimeout(() => {
    hasReset.value = false
  }, 50)
}

// 启动轮询
const startPolling = () => {
  stopPolling() // 确保先停止已存在的轮询
  polling.value = setInterval(() => {
    getLists()
  }, POLLING_INTERVAL)
}

// 停止轮询
const stopPolling = () => {
  if (polling.value) {
    clearInterval(polling.value)
    polling.value = null
  }
}

// 处理tab切换
const setActive = (button) => {
  if (button === '2') {
    message.success('当前无收藏的比赛')
    return
  }
  activeButton.value = button
  listState.state = parseInt(button)
  resetMatchSelect() // 切换tab时重置选中状态
  getLists()
  startPolling() // 切换tab时重启轮询
}

// 处理分类选择
const categorySelect = (tab) => {
  console.log('ScorePage: 分类选择', tab)
  listState.categoryId = tab.leagueIds
  resetMatchSelect()
  getLists()
  startPolling() // 切换分类时重启轮询
}

// 重置match选择状态 - 简化逻辑
const resetMatchSelect = () => {
  matchSelect.selectedMatchId = null
  matchSelect.match = {}
}

// 优化获取列表并处理选中状态的逻辑
const getLists = async (isFilterChange = false) => {
  const success = await fetchMatchList(isFilterChange)
  if (success) {
    // 优化：并行处理选中状态和动画，减少等待时间
    handleMatchSelection()
    // 只有在不是filter切换时才触发重置动画（filter切换时数据变化更明显，不需要动画）
    if (!isFilterChange) {
      triggerListReset()
    }
  } else {
    resetMatchSelect()
  }
}

// 新增：处理筛选选项变化（性能优化版本）
const handleFilterChange = async (option) => {
  // 保存旧值用于日志
  const oldOption = selectedFilterOption.value
  
  // 如果选择的是当前选项，不做处理
  if (oldOption === option) {
    console.log('选择了相同选项，不重新请求数据:', option)
    return
  }
  
  // 立即更新UI状态，让用户立即看到反馈
  selectedFilterOption.value = option
  
  const filterValue = getFilterValue(option)
  console.log('选择了过滤选项:', option, 'filter值:', filterValue, '之前选项:', oldOption)
  
  // 先停止轮询，避免并发请求
  stopPolling()
  
  // 重置选中状态（同步操作，立即执行）
  resetMatchSelect()
  
  // 重新获取列表数据（传递isFilterChange标志，优化性能）
  await getLists(true)
  
  // 重启轮询
  startPolling()
}

// 新增：点击其他区域关闭下拉菜单
const handleClickOutside = (event) => {
  const leagueDropdown = event.target.closest('.action_league_dropdown')
  if (!leagueDropdown && showLeagueDropdown.value) {
    showLeagueDropdown.value = false
  }
  
  const settingsDropdown = event.target.closest('.action_settings_dropdown')
  if (!settingsDropdown && showSettingsDropdown.value) {
    showSettingsDropdown.value = false
  }
  
  const soundDropdown = event.target.closest('.action_sound_dropdown')
  if (!soundDropdown && showSoundDropdown.value) {
    showSoundDropdown.value = false
  }
}

// 新增：切换赛事下拉菜单
const toggleLeagueDropdown = () => {
  showLeagueDropdown.value = !showLeagueDropdown.value
}

// 新增：处理联赛筛选确认
const handleLeagueConfirm = (data) => {
  console.log('筛选数据:', data)
  
  if (data.type === 'country') {
    // 国家筛选模式：按matchIds筛选比赛
    const matchIds = new Set(data.value)
    console.log('选中的比赛ID数量:', matchIds.size)
    
    // 如果选中的比赛为空，清除筛选，显示所有比赛
    if (matchIds.size === 0) {
      console.log('未选中任何国家，显示所有比赛')
      filterState.type = null
      filterState.matchIds = null
    } else {
      // 应用国家筛选
      filterState.type = 'country'
      filterState.matchIds = matchIds
      console.log('国家筛选已应用，筛选后比赛数:', plainMatches.value.length)
    }
    
  } else if (data.type === 'league') {
    // 联赛筛选模式：按联赛名称筛选
    console.log('选中的联赛:', data.value)
    
    if (!data.value || data.value.length === 0) {
      // 清除筛选
      filterState.type = null
      filterState.leagueNames = null
    } else {
      // 应用联赛筛选
      filterState.type = 'league'
      filterState.leagueNames = data.value
      console.log('联赛筛选已应用，筛选后比赛数:', plainMatches.value.length)
    }
  }
  
  // 重置match选择状态
  resetMatchSelect()
  
  // 关闭下拉菜单
  showLeagueDropdown.value = false
}

// 新增：切换设置下拉菜单
const toggleSettingsDropdown = () => {
  showSettingsDropdown.value = !showSettingsDropdown.value
  // 关闭其他下拉菜单
  if (showSettingsDropdown.value) {
    showLeagueDropdown.value = false
  }
}

// 新增：处理设置变更
const handleSettingsChange = (newSettings) => {
  console.log('设置已更新:', newSettings)
  // TODO: 根据新设置更新页面行为
  // 例如：
  // - 语言切换
  // - 排序规则变化
  // - 显示/隐藏特定信息
  // - 事件提示
}

// 新增：切换音效下拉菜单
const toggleSoundDropdown = () => {
  showSoundDropdown.value = !showSoundDropdown.value
  // 关闭其他下拉菜单
  if (showSoundDropdown.value) {
    showLeagueDropdown.value = false
    showSettingsDropdown.value = false
  }
}

// 新增：处理音效选择变更
const handleSoundChange = (sound) => {
  selectedSound.value = sound
  console.log('选择了音效:', sound, SOUND_LABEL_MAP[sound])
  // TODO: 后续实现音效播放功能
}

// 新增：处理音效按钮鼠标进入
const handleSoundMouseEnter = () => {
  showSoundDropdown.value = true
  // 关闭其他下拉框
  showSettingsDropdown.value = false
  showLeagueDropdown.value = false
}

// 新增：处理设置按钮鼠标进入
const handleSettingsMouseEnter = () => {
  showSettingsDropdown.value = true
  // 关闭其他下拉框
  showSoundDropdown.value = false
  showLeagueDropdown.value = false
}

// 新增：处理版本模式切换
const handleVersionChange = (version) => {
  versionMode.value = version
  console.log('切换版本模式:', version)
  // TODO: 根据版本模式切换不同的数据展示或布局
  // 可以在这里实现：
  // - 标准版：显示基础数据和简化布局
  // - 专业版：显示详细数据和完整功能
}

onMounted(() => {
  getFootballCetegoryLists()
  startPolling()
  getLists()
  // 添加全局点击监听，用于关闭下拉菜单
  document.addEventListener('click', handleClickOutside)
})

const getFootballCetegoryLists = () => {
  getFootballMatchCategory()
    .then((res) => {
      console.log('ScorePage: 获取分类数据', res.data)
      menusList.value = res.data
    })
    .catch((error) => {
      console.log('获取联赛分类失败:', error)
    })
}

// 组件卸载时清理轮询
onUnmounted(() => {
  stopPolling()
  // 清理全局点击监听
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped lang="scss">
.wrapper {
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 3;
  background: $bg-page;
}

.score-container {
  position: sticky;
  top: 70px;
  z-index: 10;

  background: $bg-page;

  .top {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .actions_list {
      display: flex;
      align-items: center;
      list-style: none;
      justify-content: center;
      padding: 0;
      margin: 0;
      width: 466px;

      .action_item {
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        font-family:
          PingFang SC,
          PingFang SC;
        font-weight: 500;
        font-size: 12px;
        color: $text-secondary;
        width: 72px;
        height: 30px;
        background: $bg-white;
        border-radius: 20px 20px 20px 20px;
        border: 1px solid $border-light;
        margin-right: 10px;
        transition: all 0.3s ease;

        span {
          margin-right: 4px;
          transition: color 0.3s ease;
        }

        &:hover {
          span {
            color: $color-primary;
          }
          
          .sound-icon {
            color: $color-primary;
          }
        }
      }

      // 新增：赛事下拉菜单样式
      .action_league_dropdown {
        position: relative;

        // hover 时字体变绿
        &:hover {
          background: $bg-white;
          span {
            color: $color-primary;
          }
        }
      }

      // 新增：设置下拉菜单样式
      .action_settings_dropdown {
        position: relative;

        // hover 时字体变绿
        &:hover {
          background: $bg-white;
          span {
            color: $color-primary;
          }
        }
      }

      // 新增：音效下拉菜单样式
      .action_sound_dropdown {
        position: relative;

        // hover 时字体变绿
        &:hover {
          background: $bg-white;
          span {
            color: $color-primary;
          }
          
          .sound-icon {
            color: $color-primary;
          }
        }
      }

      // 新增：下拉按钮容器样式
      .action_dropdown {
        position: relative;
      }

      // 新增：版本切换按钮容器样式
      .action_version_toggle {
        display: flex;
        align-items: center;
        margin-right: 10px;
      }

      .down-arrow,
      .settings-icon {
        width: 16px;
        height: 16px;
      }

      .sound-icon {
        width: 16px;
        height: 16px;
        color: $text-secondary;
        margin-left: 4px;
      }
    }
  }

  .category-list {
    display: flex;
    padding-bottom: 10px;
    padding-top: 10px;
  }
}

.button {
  width: 64px;
  /* 根据需要调整按钮大小 */
  height: 36px;
  /* 根据需要调整按钮大小 */
  border: 1px solid $border-color;
  border-radius: 999px;
  cursor: pointer;
  background: $bg-white;
  margin-right: 20px;
  font-weight: 500;
  font-size: 14px;
  color: $text-primary;
  box-shadow: none;
}

.button.active {
  background: $color-primary;
  border-color: transparent;
  box-shadow: 0px 10px 20px 0px rgba($color-primary, 0.25);
  color: $text-white;
  /* 在需要时添加额外的选中样式 */
}

.outer-container {
  width: 100%;
  background: $bg-page;
  box-sizing: border-box;
  min-height: 100vh;
}

.inner-container {
  width: 1200px;
  box-sizing: border-box;
  margin: 0 auto;

  background: $bg-page;
}

/* 最小宽度限制 */
@media (max-width: 1400px) {
  .inner-container {
    min-width: 1200px;
  }
}

.list {
  display: flex;
  justify-content: space-between;
  flex: 1;
  width: 100%;
  position: relative;
  transition: opacity 0.2s ease;

  .list-scroll {
    height: 100%;
    padding-right: 10px;
  }

  // 标准版全宽列表
  .list-scroll-full {
    width: 100%;
    padding-right: 0;
  }

  // 刷新状态的轻量视觉反馈
  &.refreshing {
    opacity: 0.7;
    pointer-events: none; // 刷新时禁用交互，提升性能
  }
}

.empty_list {
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  height: 70vh;
  width: 100%;
  background: $bg-white;
  color: $text-placeholder;

  .empty-image {
    width: 300px;
  }
}

.match-detail {
  // position: sticky;
  // top: 200px;
  width: 375px;
  z-index: 1;
  border-radius: 10px;
  // overflow: hidden;
  // z-index: 10;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 70vh;
  width: 100%;
}
</style>
